import React, { Suspense, useCallback, useEffect } from 'react'
import { observer } from 'mobx-react'
import { useAuth } from 'oidc-react'

import Spinner from 'Shared/atoms/Spinner'
import { usePasswordChangeRedirect } from 'Shared/hooks/usePasswordChangeRedirect'
import Modal from 'Shared/organisms/Modal'
import { AuthFields } from 'Shared/utils/auth'
import { addGlobalYupMethods } from 'Shared/utils/yupGlobalValidators'

import { UserType } from 'Core/interfaces'
import { redirectChecking } from 'Core/utils/auth'

import { LAYOUT_TYPE_USER_TYPE_DEPENDENCIES } from './utils/layout'
import { allowedUserTypes, AuthInstance } from './constants'
import Layouts from './Layouts'
import { useStores } from './stores'

import 'index.scss'

addGlobalYupMethods()

const App: React.FC = () => {
	const auth = useAuth()
	const {
		authStore: { authData, setUserData },
		profileStore: { retrieveInitialData, retrieveEhsEnabled, isEhsEnabled },
		uiStore: { isDateFormatLoading }
	} = useStores()

	const signOutRedirectCallback = useCallback(() => AuthInstance.signOutRedirect(), [])
	const clearAuthStorage = useCallback(() => AuthInstance.clearAuthStorage(), [])

	usePasswordChangeRedirect(signOutRedirectCallback, clearAuthStorage)

	redirectChecking()

	const userType = auth.userData?.profile[AuthFields.userType] as UserType

	useEffect(() => {
		retrieveInitialData().catch(() => {})
	}, [retrieveInitialData])

	useEffect(() => {
		if (userType !== UserType.NEW_CUSTOMER) {
			retrieveEhsEnabled().catch(() => {})
		}
	}, [userType, retrieveEhsEnabled])

	if (!auth.userData || !allowedUserTypes.includes(userType) || isDateFormatLoading) {
		return <Spinner isFullWindow isBrandLogo />
	}

	if (!authData) {
		setUserData(auth.userData)

		return <Spinner isFullWindow isBrandLogo />
	}

	const layoutType = LAYOUT_TYPE_USER_TYPE_DEPENDENCIES[userType]

	return (
		<>
			<Suspense fallback={<Spinner isFullWindow isBrandLogo />}>
				<Layouts layoutType={layoutType} isEhsEnabled={isEhsEnabled.value} />
			</Suspense>
			<Modal />
		</>
	)
}

export default observer(App)
